import React, { Component } from 'react';
import { bool, func, node, object, shape, string, arrayOf } from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { withRouter, Link } from 'react-router-dom';
import classNames from 'classnames';

import { injectIntl, intlShape } from '../../../../util/reactIntl';
import { getListingsById } from '../../../../ducks/marketplaceData.duck';
import { manageDisableScrolling } from '../../../../ducks/ui.duck';
import { propTypes } from '../../../../util/types';
import routeConfiguration from '../../../../routing/routeConfiguration';
import { createResourceLocatorString } from '../../../../util/routes';
import { ensureListing } from '../../../../util/data';
import { types as sdkTypes } from '../../../../util/sdkLoader';
import { ConfigurationContext } from '../../../../context/configurationContext';

import SectionContainer from '../SectionContainer';
import ClusterMap from '../../../SearchPage/SearchMap/ClusterMap/ClusterMap';
import { fetchMapListings } from './SectionMapWithListings.duck';

import css from './SectionMapWithListings.module.css';

const { LatLngBounds } = sdkTypes;

const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN;
const STATIC_MAP_URL = MAPBOX_TOKEN
  ? `https://api.mapbox.com/styles/v1/mapbox/streets-v11/static/26.0,64.8,4.8,0,0/1280x600@2x?access_token=${MAPBOX_TOKEN}`
  : null;

// Breakpoint matching --viewportMedium in CSS (768px)
const DESKTOP_BREAKPOINT = 768;

/**
 * SectionMapWithListings — interactive ClusterMap on desktop,
 * static Mapbox image (no scroll trap) on mobile.
 */
class SectionMapWithListings extends Component {
  constructor(props) {
    super(props);
    this.state = {
      activeListingId: null,
      isMapReady: false,
      isMobile: typeof window !== 'undefined' && window.innerWidth < DESKTOP_BREAKPOINT,
    };

    this.mapRefreshToken = new Date().getTime();
    this.onListingClicked = this.onListingClicked.bind(this);
    this.onMapLoad = this.onMapLoad.bind(this);
    this.createURLToListing = this.createURLToListing.bind(this);
    this.handleResize = this.handleResize.bind(this);
  }

  componentDidMount() {
    window.addEventListener('resize', this.handleResize);
    if (!this.state.isMobile) {
      this.props.onFetchMapListings();
    }
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.handleResize);
  }

  handleResize() {
    const isMobile = window.innerWidth < DESKTOP_BREAKPOINT;
    const wasDesktop = !this.state.isMobile;
    this.setState({ isMobile });
    // Fetch listings if user resized from mobile to desktop and data isn't loaded yet
    if (wasDesktop === false && !isMobile) {
      this.props.onFetchMapListings();
    }
  }

  onListingClicked(listing) {
    const { history, config } = this.props;
    if (listing) {
      const routes = routeConfiguration(config.layout, config.accessControl);
      const listingURL = this.createURLToListing(ensureListing(listing));
      history.push(listingURL);
    }
  }

  onMapLoad(map) {
    this.setState({ isMapReady: !!map });
  }

  createURLToListing(listing) {
    const { config } = this.props;
    const routes = routeConfiguration(config.layout, config.accessControl);
    const id = listing.id.uuid;
    const slug = listing.attributes.title
      ? listing.attributes.title
          .toLowerCase()
          .replace(/\s+/g, '-')
          .replace(/[^\w-]/g, '')
      : id;
    return createResourceLocatorString('ListingPage', routes, { id, slug }, {});
  }

  renderMobileStatic() {
    const { sectionId, className, rootClassName, defaultClasses, title, description, appearance, options } = this.props;
    return (
      <SectionContainer
        id={sectionId}
        className={className}
        rootClassName={classNames(rootClassName || css.root)}
        appearance={appearance}
        options={options}
      >
        <div className={css.mapContainer}>
          {title && <h2 className={defaultClasses?.title || css.title}>{title.content}</h2>}
          {description && (
            <p className={defaultClasses?.description || css.description}>{description.content}</p>
          )}
          <Link to="/s" className={css.mapWrapperStatic} aria-label="Siirry hakuun">
            {STATIC_MAP_URL ? (
              <img src={STATIC_MAP_URL} alt="Kartta palveluista" className={css.mapImage} />
            ) : (
              <div className={css.mapFallback} />
            )}
            <div className={css.searchOverlay}>
              <div className={css.searchButton}>
                <svg className={css.searchIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="11" cy="11" r="8" />
                  <line x1="21" y1="21" x2="16.65" y2="16.65" />
                </svg>
                <span>Etsi palveluita</span>
              </div>
            </div>
          </Link>
        </div>
      </SectionContainer>
    );
  }

  render() {
    const { isMobile } = this.state;

    if (isMobile) {
      return this.renderMobileStatic();
    }

    const {
      sectionId,
      className,
      rootClassName,
      defaultClasses,
      title,
      description,
      appearance,
      options,
      listings,
      fetchInProgress,
      isFromCache,
    } = this.props;

    const { activeListingId } = this.state;

    const calculateBounds = listings => {
      if (!listings || listings.length === 0) {
        return new LatLngBounds(
          new sdkTypes.LatLng(70.0, 31.5),
          new sdkTypes.LatLng(59.5, 19.5)
        );
      }

      let minLat = 90, maxLat = -90, minLng = 180, maxLng = -180;
      listings.forEach(listing => {
        const geo = listing.attributes.geolocation;
        if (geo) {
          minLat = Math.min(minLat, geo.lat);
          maxLat = Math.max(maxLat, geo.lat);
          minLng = Math.min(minLng, geo.lng);
          maxLng = Math.max(maxLng, geo.lng);
        }
      });

      const latPadding = (maxLat - minLat) * 0.25 || 1.5;
      const lngPadding = (maxLng - minLng) * 0.25 || 2.0;

      return new LatLngBounds(
        new sdkTypes.LatLng(maxLat + latPadding, maxLng + lngPadding),
        new sdkTypes.LatLng(minLat - latPadding, minLng - lngPadding)
      );
    };

    const bounds = calculateBounds(listings);
    const isLoading = fetchInProgress && !isFromCache;

    const getLoadingMessage = () => {
      if (isFromCache) return 'Loading map from cache...';
      if (listings.length === 0) return 'Loading first listings...';
      if (listings.length < 200) return `Loaded ${listings.length} listings... (phase 1/3)`;
      if (listings.length < 1000) return `Loaded ${listings.length} listings... (phase 2/3)`;
      return `Loaded ${listings.length} listings... (phase 3/3)`;
    };

    return (
      <SectionContainer
        id={sectionId}
        className={className}
        rootClassName={classNames(rootClassName || css.root)}
        appearance={appearance}
        options={options}
      >
        <div className={css.mapContainer}>
          {title && <h2 className={defaultClasses?.title || css.title}>{title.content}</h2>}
          {description && (
            <p className={defaultClasses?.description || css.description}>{description.content}</p>
          )}
          <div className={css.mapWrapper}>
            {isLoading && listings.length === 0 ? (
              <div className={css.loading}>
                <div>🗺️ Loading map...</div>
                <div style={{ fontSize: '12px', marginTop: '8px', opacity: 0.7 }}>Phase 1 of 3</div>
              </div>
            ) : (
              <>
                <ClusterMap
                  bounds={bounds}
                  center={null}
                  listings={listings}
                  activeListingId={activeListingId}
                  onMapLoad={this.onMapLoad}
                  onListingClicked={this.onListingClicked}
                />
                {isLoading && listings.length > 0 && (
                  <div className={css.loadingOverlay} style={{ position: 'absolute', bottom: '20px', right: '20px', backgroundColor: 'rgba(0,0,0,0.7)', color: 'white', padding: '10px 15px', borderRadius: '4px', fontSize: '12px' }}>
                    {getLoadingMessage()}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </SectionContainer>
    );
  }
}

SectionMapWithListings.defaultProps = {
  className: null,
  rootClassName: null,
  defaultClasses: null,
  title: null,
  description: null,
  appearance: null,
  options: null,
};

SectionMapWithListings.propTypes = {
  sectionId: string.isRequired,
  className: string,
  rootClassName: string,
  defaultClasses: shape({
    title: string,
    description: string,
  }),
  title: object,
  description: object,
  appearance: object,
  options: shape({
    fieldComponents: shape({ component: node, pickValidProps: func }),
  }),
  location: object,
  config: object.isRequired,
  intl: intlShape.isRequired,
  history: object.isRequired,
  listings: arrayOf(shape({
    id: shape({ uuid: string.isRequired }).isRequired,
    type: string.isRequired,
    attributes: object.isRequired
  })),
  fetchInProgress: bool.isRequired,
  onFetchMapListings: func.isRequired,
};

const mapStateToProps = state => {
  const { listingIds, fetchInProgress, isFromCache } = state.LandingPageMap || {};
  const fullListings = getListingsById(state, listingIds || []);
  const listings = fullListings.map(listing => {
    const idString = typeof listing.id === 'string' ? listing.id : listing.id?.uuid;
    return {
      id: { uuid: idString },
      type: 'listing',
      attributes: {
        title: listing.attributes?.title,
        geolocation: listing.attributes?.geolocation,
        price: listing.attributes?.price,
        publicData: listing.attributes?.publicData,
        state: listing.attributes?.state
      }
    };
  });
  return {
    listings,
    fetchInProgress: fetchInProgress || false,
    isFromCache: isFromCache || false,
  };
};

const mapDispatchToProps = dispatch => ({
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
  onFetchMapListings: () => dispatch(fetchMapListings()),
});

const withConfiguration = Component => {
  return props => (
    <ConfigurationContext.Consumer>
      {config => <Component {...props} config={config} />}
    </ConfigurationContext.Consumer>
  );
};

export default compose(
  withRouter,
  connect(mapStateToProps, mapDispatchToProps),
  injectIntl,
  withConfiguration
)(SectionMapWithListings);
